<script lang="ts" setup>
const documentId = inject("documentId") as any;

const documentParagraphsStore = useDocumentParagraphsStore(documentId);
import ConfirmDialog from "primevue/confirmdialog";
import { useConfirm } from "primevue/useconfirm";

const { paragraphs } = storeToRefs(documentParagraphsStore);

const documentViewModeStore = useDocumentViewModeStore(documentId);
const { isTeacherViewMode, showChecklistSummary, isConsolidatedMode } =
  storeToRefs(documentViewModeStore);

const isInitialized = ref(false);

const confirm = useConfirm();

import { AnnotationTypes } from "~/types/enums/AnnotationTypes.enum";

onMounted(async () => {
  await documentParagraphsStore.initializeParagraphs();
  useDocumentEvaluativeParagraphsStore(documentId).streamEvaluativeParagraphs();

  isInitialized.value = true;

  if (
    submittedDocument.value?.state != DocumentState.editing &&
    documentParagraphsStore.isMissingAssignment
  ) {
    confirm.require({
      group: "fix-assignment",
      header: "Fix Assignment",
      message:
        "We were unable to detect the student's assignment. The Writing Assignment header may have been removed. Would you like to fix the assignment?",
      acceptLabel: "Fix Assignment",
      rejectLabel: "Cancel",
      acceptClass: " p-2 w-[150px] bg-primary-default text-white rounded-md",
      rejectClass: " text-danger-default p-2 w-[150px] rounded-md",

      accept: () => {
        // Turn on fix assignmnte mode
        documentParagraphsStore.toggleIsFixingAssignment(true);
      },
    });
  }
});

const documentSelectionStore = useDocumentSelectionStore(documentId);
const { setSelection, clearSelection } = documentSelectionStore;
const { isTokenSelected, startPosition, endPosition } = storeToRefs(
  documentSelectionStore
);

const clearHighlightedAnnotation = () => {
  const { setHoverAnnotation } = useAnnotationTokenHighlight();
  setHoverAnnotation(undefined);
};

const { setAnnotationBeingEdited, createDefaultAnnotation } =
  useAnnotationFormState();

const { saveAnnotation } = useSaveAnnotation(documentId);

const onKeyUp = (event: KeyboardEvent) => {
  if (event.key == "Escape") {
    clearHighlightedAnnotation();
  }

  // if (event.key == "Enter") {
  //   // ok here I need to call the option to open the annotation form. Save as the quick action
  //   const commentOnlyAnnotation = createDefaultAnnotation();
  //   setAnnotationBeingEdited(commentOnlyAnnotation);
  // }

  if (event.key == ".") {
    // Here'r I'll add the period annotation just ike a quick action
    const periodAnnotation = createDefaultAnnotation(
      useAnnotationType(AnnotationTypes.insertPeriod)
    );
    saveAnnotation(periodAnnotation);
  }

  if (event.key == ",") {
    // Here I'll add the annotation like teh comman quick action.
    const commaAnnotation = createDefaultAnnotation(
      useAnnotationType(AnnotationTypes.insertComma)
    );
    saveAnnotation(commaAnnotation);
  }

  if (event.key == "Backspace") {
    const { formatSelectedTokens } = useTokenFormatter();
    formatSelectedTokens(documentId, { strikeThrough: true });
  }
};

const onKeyDown = (event: KeyboardEvent) => {
  try {
    if (
      isTeacherViewMode.value == true &&
      ((isTeacherViewMode.value == true && event.key == "x") ||
        event.key == "s") &&
      (event.metaKey || event.ctrlKey)
    ) {
      const { formatSelectedTokens, doesSelectionContainFormatting } =
        useTokenFormatter();
      formatSelectedTokens(documentId, {
        strikeThrough: !doesSelectionContainFormatting(
          documentId,
          "strikeThrough"
        ),
      });
    }

    if (
      isTeacherViewMode.value == true &&
      event.key == "b" &&
      (event.metaKey || event.ctrlKey)
    ) {
      const { formatSelectedTokens, doesSelectionContainFormatting } =
        useTokenFormatter();
      formatSelectedTokens(documentId, {
        bold: !doesSelectionContainFormatting(documentId, "bold"),
      });
    }

    if (
      isTeacherViewMode.value == true &&
      event.key == "u" &&
      (event.metaKey || event.ctrlKey)
    ) {
      const { formatSelectedTokens, doesSelectionContainFormatting } =
        useTokenFormatter();
      formatSelectedTokens(documentId, {
        underline: !doesSelectionContainFormatting(documentId, "underline"),
      });
    }

    if (
      isTeacherViewMode.value == true &&
      event.key == "i" &&
      (event.metaKey || event.ctrlKey)
    ) {
      const { formatSelectedTokens, doesSelectionContainFormatting } =
        useTokenFormatter();
      formatSelectedTokens(documentId, {
        italic: !doesSelectionContainFormatting(documentId, "italic"),
      });
    }

    if (
      isTeacherViewMode.value == true &&
      event.key == "ArrowLeft" &&
      startPosition.value != undefined
    ) {
      // going to change the selection one over to the left I guess we just decide if event.meta is enabled.

      if (event.metaKey != true && event.ctrlKey != true) {
        var newStartPosition = useDocumentTraversalStore(
          documentId
        ).getPreviousTokenPosition(startPosition.value);

        if (newStartPosition != undefined) {
          selectToken(newStartPosition);
        }
      }

      if (event.metaKey == true || event.ctrlKey == true) {
        // ok here we're going to select the next token
        const newLinePosition = useDocumentTraversalStore(
          documentId
        ).getStartLinePosition(startPosition.value);

        if (newLinePosition != undefined) {
          selectToken(newLinePosition);
        }
      }

      event.preventDefault();
      event.stopPropagation();
    }

    if (
      isTeacherViewMode.value == true &&
      event.key == "ArrowRight" &&
      endPosition.value != undefined
    ) {
      // going to change the selection one over to the right

      if (event.metaKey != true && event.ctrlKey != true) {
        var newEndPosition = useDocumentTraversalStore(
          documentId
        ).getNextTokenPosition(endPosition.value);

        if (newEndPosition != undefined) {
          selectToken(newEndPosition);
        }
      }

      if (event.metaKey == true || event.ctrlKey == true) {
        // ok here we're going to select the next token
        const newLinePosition = useDocumentTraversalStore(
          documentId
        ).getEndLinePosition(endPosition.value);

        if (newLinePosition != undefined) {
          selectToken(newLinePosition);
        }
      }

      event.preventDefault();
      event.stopPropagation();
    }

    if (
      isTeacherViewMode.value == true &&
      event.key == "ArrowDown" &&
      endPosition.value != undefined
    ) {
      const newLinePosition = useDocumentTraversalStore(
        documentId
      ).getNextLineGlobalPosition(endPosition.value);

      if (newLinePosition != undefined) {
        selectToken(newLinePosition);
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (
      isTeacherViewMode.value == true &&
      event.key == "ArrowUp" &&
      endPosition.value != undefined
    ) {
      const newLinePosition = useDocumentTraversalStore(
        documentId
      ).getPreviousLineGlobalPosition(endPosition.value);

      if (newLinePosition != undefined) {
        selectToken(newLinePosition);
      }

      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (
      (event.key == " " || event.key == "Enter") &&
      startPosition.value != undefined
    ) {
      event.preventDefault();
      event.stopPropagation();
      useCreateInlineEditingAnnotation(documentId);
    }
  } catch (error) {
    // Empty
  }
};

const paragraphsContainer = ref();

const { focused } = useFocus(paragraphsContainer);

const selectToken = (tokenGlobalPosition: number) => {
  useSelectTokenByPosition(tokenGlobalPosition);
};

const submittedDocumentStore = useSubmittedDocumentStore(documentId);
const { submittedDocument, revision, isEditingMode } = storeToRefs(
  submittedDocumentStore
);

const doesExpectAssignment = computed(() => {
  return submittedDocument.value?.doesNOTExpectAssignment != true;
});

const showChecklist = computed(() => {
  return (
    showChecklistSummary.value == true &&
    submittedDocument.value?.state == DocumentState.graded
  );
});

const revisionCommentPlacement = computed(() => {
  return (
    revision.value?.commentPlacement ?? RevisionCommentPlacement.AfterDocument
  );
});

const canEditDocument = computed(() => {
  if (isTeacherViewMode.value == true) return false;

  if (submittedDocument.value?.classroomId == undefined) return false;

  if (submittedDocument.value.state != DocumentState.studentReview)
    return false;

  return true;
});
</script>

<template>
  <div
    data-step-id="paragraphs-container"
    class="bg-surface focus:outline-none selection:bg-none"
    :class="{
      ' pt-4': isConsolidatedMode,
    }"
  >
    <ConfirmDialog group="fix-assignment" class="max-w-[500px]" />

    <div class="w-full h-full flex flex-col" v-if="isInitialized != true">
      <BaseSkeleton class="h-[18px] w-[130px] mb-4" />
      <BaseSkeleton class="h-[18px] w-[130px] mb-4" />
      <BaseSkeleton class="h-[18px] w-[200px] mx-auto mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-[20%] mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-[20%] mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-full mb-4" />
      <BaseSkeleton class="h-[18px] w-[20%] mb-4" />
    </div>

    <div
      v-if="isInitialized == true"
      class="w-full focus:outline-none"
      tabindex="0"
      ref="paragraphsContainer"
      id="paragraphs-container"
      @keydown="onKeyDown"
      @keyup="onKeyUp"
      v-auto-animate
    >
      <div v-if="canEditDocument" class="flex flex-row justify-end">
        <DocumentParagraphsStudentEditingButton
          v-if="
            submittedDocument?.id &&
            submittedDocument?.classroomId &&
            submittedDocument?.studentId
          "
          :submittedDocumentId="submittedDocument.id"
          :classroomId="submittedDocument.classroomId"
          :studentId="submittedDocument.studentId"
        />
      </div>
      <div
        v-if="isEditingMode != true"
        class="relative"
        id="paragraphs-position-container"
      >
        <!-- If we're graded and we're in student mode then we'll show a new checklist summary component. -->
        <div v-if="paragraphs.length == 0 && doesExpectAssignment == true">
          No paragraphs found.
        </div>
        <DocumentHeaders />
        <DocumentChecklistSummary v-if="showChecklist" />
        <DocumentGeneralFeedback
          v-if="
            paragraphs &&
            revisionCommentPlacement == RevisionCommentPlacement.BeforeDocument
          "
          class="mb-8"
        />
        <DocumentParagraphsParagraph
          v-for="paragraph in paragraphs"
          :key="`${paragraph.id}-${paragraph.text}`"
          :paragraph="paragraph"
        />
        <DocumentParagraphsTokenOverlays />
        <DocumentGeneralFeedback
          v-if="
            paragraphs &&
            revisionCommentPlacement == RevisionCommentPlacement.AfterDocument
          "
        />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
